import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { 
  FileText, 
  Download, 
  Printer, 
  Calendar, 
  Activity, 
  Droplets,
  AlertCircle,
  CheckCircle,
  X
} from "lucide-react";
import { useAuth } from "./AuthContext";
import { getWaterByDateRange, getProgressByDateRange, getSymptomsByDateRange } from "./partnerApi";

export default function DoctorPrep({ onClose }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [dateRange, setDateRange] = useState("30"); // days

  const generateReport = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    const days = parseInt(dateRange);
    const endDate = new Date().toISOString().split("T")[0];
    const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
    
    try {
      const [progress, symptoms, water] = await Promise.all([
        getProgressByDateRange(user.id, startDate, endDate),
        getSymptomsByDateRange(user.id, startDate, endDate),
        getWaterByDateRange(user.id, startDate, endDate)
      ]);
      
      // Calculate completion rate
      const totalDays = days;
      let completedDays = 0;
      if (progress) {
        const dates = Object.keys(progress);
        completedDays = dates.filter(date => {
          const dayData = progress[date];
          return Object.values(dayData).some(Boolean);
        }).length;
      }
      const completionRate = Math.round((completedDays / totalDays) * 100);
      
      // Count symptoms
      const symptomCounts = {};
      if (symptoms) {
        symptoms.forEach(s => {
          if (s.symptoms) {
            s.symptoms.forEach(symptom => {
              symptomCounts[symptom] = (symptomCounts[symptom] || 0) + 1;
            });
          }
        });
      }
      
      // Average water intake
      const avgWater = water && water.length > 0 
        ? Math.round(water.reduce((sum, w) => sum + (w.cups || 0), 0) / water.length)
        : 0;
      
      setReport({
        dateRange: { start: startDate, end: endDate },
        completionRate,
        totalDays,
        completedDays,
        symptomCounts,
        avgWater,
        recentSymptoms: symptoms?.slice(-7) || [],
        progressData: progress || {}
      });
    } catch (err) {
      console.error("Error generating report:", err);
    }
    
    setLoading(false);
  }, [user, dateRange]);

  useEffect(() => {
    generateReport();
  }, [generateReport]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    // Generate text report
    const text = generateTextReport();
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pmos-report-${report.dateRange.start}-to-${report.dateRange.end}.txt`;
    a.click();
  };

  const generateTextReport = () => {
    if (!report) return "";
    
    return `
════════════════════════════════════════════════════════════
                    PMOS HEALTH REPORT
                    For Doctor Appointment
════════════════════════════════════════════════════════════

Report Period: ${report.dateRange.start} to ${report.dateRange.end}
Generated: ${new Date().toLocaleDateString()}

────────────────────────────────────────────────────────────
                    SUMMARY
────────────────────────────────────────────────────────────

Tracking Consistency: ${report.completionRate}%
Days Tracked: ${report.completedDays} / ${report.totalDays}
Average Water Intake: ${report.avgWater} cups/day

────────────────────────────────────────────────────────────
                SYMPTOM FREQUENCY
────────────────────────────────────────────────────────────

${Object.entries(report.symptomCounts)
  .sort((a, b) => b[1] - a[1])
  .map(([symptom, count]) => `${symptom}: ${count} times`)
  .join("\n") || "No symptoms logged in this period."}

────────────────────────────────────────────────────────────
                QUESTIONS TO ASK
────────────────────────────────────────────────────────────

1. Based on my symptoms, what phenotype of PMOS might I have?
2. Are there any patterns in my cycle/symptoms you notice?
3. What treatment adjustments might help with my most frequent symptoms?
4. Should I be concerned about [most frequent symptom]?
5. Are there any tests or referrals you'd recommend?

────────────────────────────────────────────────────────────
                NOTES
────────────────────────────────────────────────────────────

[Space for doctor's notes]

════════════════════════════════════════════════════════════
Generated by Laiba Health App
════════════════════════════════════════════════════════════
`.trim();
  };

  const topSymptoms = report?.symptomCounts 
    ? Object.entries(report.symptomCounts).sort((a, b) => b[1] - a[1]).slice(0, 5)
    : [];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 print:bg-white print:p-0">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-3xl shadow-2xl print:shadow-none print:rounded-none print:max-h-none"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6 print:bg-white print:border-b">
          <div className="flex items-center justify-between print:hidden">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-white" />
              <div>
                <h2 className="text-2xl font-bold text-white">Doctor Prep Report</h2>
                <p className="text-emerald-100">Be heard in your 15 minutes</p>
              </div>
            </div>
            <button onClick={onClose} className="text-white/70 hover:text-white transition">
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {/* Print header */}
          <div className="hidden print:block text-center">
            <h1 className="text-2xl font-bold text-slate-800">PMOS Health Report</h1>
            <p className="text-slate-600">For Doctor Appointment</p>
          </div>

          {/* Date Range Selector */}
          <div className="mt-4 flex gap-2 print:hidden">
            {[
              { label: "7 Days", value: "7" },
              { label: "30 Days", value: "30" },
              { label: "90 Days", value: "90" }
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => setDateRange(option.value)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                  dateRange === option.value
                    ? "bg-white text-emerald-600"
                    : "bg-white/20 text-white hover:bg-white/30"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-slate-600">Analyzing your data...</p>
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-4 print:grid-cols-3">
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-4 text-center border border-emerald-100 print:border">
                <Activity className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-emerald-600">{report?.completionRate || 0}%</p>
                <p className="text-sm text-slate-600">Consistency</p>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-4 text-center border border-blue-100 print:border">
                <Droplets className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-blue-600">{report?.avgWater || 0}</p>
                <p className="text-sm text-slate-600">Avg Water/Day</p>
              </div>
              
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-4 text-center border border-purple-100 print:border">
                <Calendar className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-purple-600">{report?.completedDays || 0}</p>
                <p className="text-sm text-slate-600">Days Tracked</p>
              </div>
            </div>

            {/* Top Symptoms */}
            <div className="bg-slate-50 rounded-2xl p-5 print:border">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                Most Frequent Symptoms
              </h3>
              
              {topSymptoms.length > 0 ? (
                <div className="space-y-3">
                  {topSymptoms.map(([symptom, count], idx) => (
                    <div key={symptom} className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-sm font-medium">
                        {idx + 1}
                      </span>
                      <span className="flex-1 text-slate-700">{symptom}</span>
                      <span className="text-slate-500 text-sm">{count}x</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-sm">No symptoms logged yet. Start tracking to see patterns!</p>
              )}
            </div>

            {/* Questions to Ask */}
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-5 border border-indigo-100 print:border">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-indigo-500" />
                Questions to Ask Your Doctor
              </h3>
              
              <ol className="space-y-2 text-slate-700">
                <li className="flex gap-2">
                  <span className="text-indigo-600 font-medium">1.</span>
                  Based on my symptoms, what phenotype of PMOS might I have?
                </li>
                <li className="flex gap-2">
                  <span className="text-indigo-600 font-medium">2.</span>
                  Are there patterns in my cycle/symptoms you notice?
                </li>
                <li className="flex gap-2">
                  <span className="text-indigo-600 font-medium">3.</span>
                  What treatment adjustments might help with my most frequent symptoms?
                </li>
                <li className="flex gap-2">
                  <span className="text-indigo-600 font-medium">4.</span>
                  Should I be concerned about [most frequent symptom]?
                </li>
                <li className="flex gap-2">
                  <span className="text-indigo-600 font-medium">5.</span>
                  Are there any tests or referrals you'd recommend?
                </li>
              </ol>
            </div>

            {/* Notes Section */}
            <div className="bg-slate-50 rounded-2xl p-5 print:border">
              <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5 text-slate-500" />
                Notes for Doctor
              </h3>
              <div className="min-h-[100px] border border-dashed border-slate-300 rounded-lg p-3">
                <p className="text-slate-400 text-sm italic">Write any additional concerns here...</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 print:hidden">
              <button
                onClick={handleDownload}
                className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl py-3 font-medium flex items-center justify-center gap-2 hover:shadow-lg transition"
              >
                <Download className="w-5 h-5" />
                Download Report
              </button>
              <button
                onClick={handlePrint}
                className="flex-1 bg-slate-100 text-slate-700 rounded-xl py-3 font-medium flex items-center justify-center gap-2 hover:bg-slate-200 transition"
              >
                <Printer className="w-5 h-5" />
                Print
              </button>
            </div>

            {/* Footer */}
            <p className="text-center text-slate-400 text-sm print:hidden">
              Generated by Laiba Health App • {new Date().toLocaleDateString()}
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
}